import { useCallback, useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useTier } from './useTier';

// Owns widget/Siri discovery triggers + dismissal state.
//
// Gates:
//   - iOS native only (Capacitor.getPlatform() === 'ios')
//   - Pro tier only — free users churn before widget retention matters,
//     and we want to keep the conversion-critical first sessions quiet.
//   - Each prompt fires at most once (localStorage flag)
//
// Triggers:
//   - markScanConfirmed()  → bumps scan counter. First call (and only
//     the first) opens the widget dialog.
//   - 3+ scans → flips a flag for a one-time Siri-tip Snackbar.
//
// External components fire the trigger via:
//   window.dispatchEvent(new CustomEvent('fueltron:scan-confirmed'))
// to avoid prop-drilling the markScanConfirmed callback into ScanPage.
//
// Read flags:
//   - widgetDialogOpen / closeWidgetDialog: drive WidgetDiscoveryDialog
//   - shouldShowSiriTip / dismissSiriTip: drive the Dashboard Snackbar
//   - openWidgetDialog(): manual reopen from Profile Settings

const LS_WIDGET_PROMPTED = 'fueltron_widget_prompted_v1';
const LS_SIRI_TIP_DISMISSED = 'fueltron_siri_tip_dismissed_v1';
const LS_SCAN_COUNT = 'fueltron_scan_count_v1';

const SIRI_TIP_AT_SCAN = 3;

const isIOSNative = () =>
  typeof window !== 'undefined' && Capacitor.getPlatform() === 'ios';

const safeRead = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeWrite = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* localStorage disabled */
  }
};

export function useWidgetDiscovery() {
  const [widgetDialogOpen, setWidgetDialogOpen] = useState(false);
  const [siriTipOpen, setSiriTipOpen] = useState(false);
  const { isPro } = useTier();
  const eligible = isIOSNative() && isPro;

  // On mount (and whenever Pro status changes), check if the user has
  // crossed the Siri-tip threshold but hasn't been shown the tip yet.
  useEffect(() => {
    if (!eligible) return;
    if (safeRead(LS_SIRI_TIP_DISMISSED)) return;
    const scans = parseInt(safeRead(LS_SCAN_COUNT) ?? '0', 10);
    if (scans >= SIRI_TIP_AT_SCAN) {
      setSiriTipOpen(true);
    }
  }, [eligible]);

  // Internal handler — fires on the scan-confirmed event. First-ever
  // success opens the widget dialog; further successes accumulate
  // toward the Siri-tip threshold.
  const markScanConfirmedInternal = useCallback(() => {
    if (!eligible) return;

    const prevCount = parseInt(safeRead(LS_SCAN_COUNT) ?? '0', 10);
    const newCount = prevCount + 1;
    safeWrite(LS_SCAN_COUNT, String(newCount));

    if (!safeRead(LS_WIDGET_PROMPTED)) {
      // Small delay so the dialog doesn't slam in over the confirmation
      // animation — gives the user a second to feel the success first.
      setTimeout(() => setWidgetDialogOpen(true), 1200);
      safeWrite(LS_WIDGET_PROMPTED, '1');
    } else if (
      newCount >= SIRI_TIP_AT_SCAN &&
      !safeRead(LS_SIRI_TIP_DISMISSED)
    ) {
      setSiriTipOpen(true);
    }
  }, [eligible]);

  // Listen for the cross-component trigger event from ScanPage.
  // Profile's "Show me how" button bypasses the Pro gate intentionally —
  // if a Pro user navigated to that card and tapped it, just show.
  useEffect(() => {
    const handleScan = () => markScanConfirmedInternal();
    const handleOpen = () => {
      if (eligible) setWidgetDialogOpen(true);
    };
    window.addEventListener('fueltron:scan-confirmed', handleScan);
    window.addEventListener('fueltron:open-widget-dialog', handleOpen);
    return () => {
      window.removeEventListener('fueltron:scan-confirmed', handleScan);
      window.removeEventListener('fueltron:open-widget-dialog', handleOpen);
    };
  }, [eligible, markScanConfirmedInternal]);

  const closeWidgetDialog = useCallback(() => setWidgetDialogOpen(false), []);

  // Manual reopen path — from Profile Settings row.
  const openWidgetDialog = useCallback(() => setWidgetDialogOpen(true), []);

  const dismissSiriTip = useCallback(() => {
    safeWrite(LS_SIRI_TIP_DISMISSED, '1');
    setSiriTipOpen(false);
  }, []);

  return {
    isIOS: isIOSNative(),
    widgetDialogOpen,
    siriTipOpen,
    closeWidgetDialog,
    openWidgetDialog,
    dismissSiriTip,
  };
}

// Fire from anywhere — e.g. ScanPage after a successful confirm.
// Safe to call on non-iOS: the hook ignores when not on iOS native.
export function fireScanConfirmed(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('fueltron:scan-confirmed'));
}

// Open the widget discovery dialog from anywhere (e.g. Profile Settings row).
// Caller is responsible for gating on Pro/iOS via useTier + isIOSNative();
// the hook itself ignores opens for ineligible users.
export function openWidgetDiscoveryDialog(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('fueltron:open-widget-dialog'));
}

// Plain function — true on native iOS. Pro check belongs in the caller
// (use the useTier hook in JSX) so we don't make this a hook.
export function isIOSNativePlatform(): boolean {
  return isIOSNative();
}
